import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "redux/store";
import Button from "style-guide/component/Button";
import Typography from "style-guide/component/Typography";
import { verify2FA } from "../../redux/slice/rfpModuleSlice/application";
import Gradient from "style-guide/component/Gradient";

const TwoFactorAuth: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [isResending, setIsResending] = useState(false);

  const { isLoading, requires2FA, userEmail } = useSelector(
    (state: RootState) => state.applicationData
  );

  // Redirect if 2FA is not required
  useEffect(() => {
    if (!requires2FA) {
      navigate("/");
    }
  }, [requires2FA, navigate]);

  const validateCode = (code: string) => {
    return /^\d{6}$/.test(code);
  };

  const handleVerify = async () => {
    setCodeError("");

    if (!code) {
      setCodeError("Please enter the 6-digit code.");
      return;
    }

    if (!validateCode(code)) {
      setCodeError("Please enter a valid 6-digit code.");
      return;
    }

    const result = await dispatch(verify2FA(code, userEmail || ""));
    if (result?.status === 200) {
      navigate("/home");
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    // Here you would typically call an API to resend the code
    // For now, we'll just show a message
    setTimeout(() => {
      setIsResending(false);
    }, 2000);
  };

  const handleBackToLogin = () => {
    navigate("/");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleVerify();
    }
  };

  if (!requires2FA) {
    return null;
  }

  return (
    <Gradient>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg space-y-6 mt-10">
          <div className="text-center">
            <Typography
              text="Two-Factor Authentication"
              variant="Heading 3"
              className="text-blue-900 font-bold mb-2"
            />
            <Typography
              text="Enter the 6-digit code sent to your email"
              variant="Base"
              className="text-gray-600"
            />
          </div>

          <div className="space-y-4">
            <div>
              <Typography
                text="Verification Code"
                variant="Base"
                className="mb-2 text-gray-700"
              />
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={6}
                className="w-full px-4 py-2 text-center text-lg tracking-widest border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-colors"
              />
              {codeError && (
                <Typography variant="Base" className="text-red-500 mt-1">
                  {codeError}
                </Typography>
              )}
            </div>

            <Button
              type="Primary"
              icon={false}
              onClick={handleVerify}
              buttonText={isLoading ? "Verifying..." : "Verify Code"}
              theme="light"
              className="w-full py-3 hover:bg-blue-700 rounded-lg"
              loading={isLoading}
            />

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {isResending ? "Resending..." : "Resend Code"}
              </button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-gray-600 hover:text-gray-800"
              >
                Back to Login
              </button>
            </div>

            <Typography
              variant="Small"
              className="text-center text-gray-500 text-xs"
            >
              Didn't receive the code? Check your spam folder or contact
              support.
            </Typography>
          </div>
        </div>
    </Gradient>
  );
};

export default TwoFactorAuth;
